import React, { useState, useEffect, useCallback } from 'react';
import './ProgressiveBackground.css';

interface ProgressiveBackgroundProps {
  imageName: string;  // Image file name without extension
  className?: string; // Additional CSS classes
  ariaLabel?: string; // Accessibility
}

const ProgressiveBackground: React.FC<ProgressiveBackgroundProps> = ({
  imageName,
  className = '',
  ariaLabel
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Determine which image size to load based on screen width
  const getImageSize = useCallback(() => {
    if (windowWidth <= 768) return 'small';
    if (windowWidth <= 1280) return 'medium';
    return 'large';
  }, [windowWidth]);

  // Preload the main image
  useEffect(() => {
    const size = getImageSize();
    const imgLoader = new Image();
    
    try {
      // First try WebP
      const webpSrc = require(`../assets/optimized/${imageName}-${size}.webp`);
      imgLoader.src = webpSrc;
      
      imgLoader.onload = () => {
        setIsLoaded(true);
      };
      
      imgLoader.onerror = () => {
        // If WebP is not supported, load JPEG
        try {
          const jpgLoader = new Image();
          const jpgSrc = require(`../assets/optimized/${imageName}-${size}.jpg`);
          jpgLoader.src = jpgSrc;
          
          jpgLoader.onload = () => {
            setIsLoaded(true);
          };
        } catch (err) {
          console.error('Error loading JPEG version:', err);
        }
      };
    } catch (err) {
      console.error('Error loading image:', err);
    }
  }, [imageName, windowWidth, getImageSize]);

  // Safely get image URLs
  const getImageUrl = (name: string, size: string): string => {
    try {
      return require(`../assets/optimized/${name}-${size}.webp`);
    } catch (err) {
      try {
        return require(`../assets/optimized/${name}-${size}.jpg`);
      } catch (err) {
        console.error(`Failed to load image ${name}-${size}`, err);
        return '';
      }
    }
  };

  const previewUrl = getImageUrl(imageName, 'preview');
  const mainUrl = isLoaded ? getImageUrl(imageName, getImageSize()) : '';
  
  return (
    <div 
      className={`progressive-background ${className} ${isLoaded ? 'loaded' : ''}`} 
      role="img" 
      aria-label={ariaLabel}
    >
      <div 
        className="preview-bg"
        style={{ backgroundImage: `url(${previewUrl})` }}
      />
      {isLoaded && (
        <div 
          className="main-bg"
          style={{ backgroundImage: `url(${mainUrl})` }}
        />
      )}
    </div>
  );
};

export default ProgressiveBackground; 